import Link from "next/link";

import type { ComparisonRow } from "@/content/comparisons/schema";

interface QuickComparisonTableProps {
  heading: string;
  competitorName: string;
  rows: ComparisonRow[];
}

const DOT_POSITIONS = [
  "left-[8%] top-[20%] h-2 w-2",
  "right-[10%] top-[12%] h-1.5 w-1.5",
  "left-[5%] bottom-[25%] h-2.5 w-2.5",
  "right-[7%] bottom-[15%] h-2 w-2",
  "left-[15%] bottom-[50%] h-1.5 w-1.5",
  "right-[18%] top-[45%] h-1.5 w-1.5",
] as const;

export function QuickComparisonTable({ heading, competitorName, rows }: QuickComparisonTableProps) {
  const headingParts = heading.split("Rebolt");
  const hasReboltInHeading = headingParts.length > 1;

  return (
    <section className="relative overflow-hidden bg-ash-100 py-16 md:py-24">
      {DOT_POSITIONS.map((pos, i) => (
        <span key={i} className={`absolute hidden rounded-full bg-rebolt-500/25 md:block ${pos}`} />
      ))}

      <div className="relative z-10 mx-auto max-w-4xl px-6">
        <h2 className="mb-12 text-center font-clash text-3xl font-medium leading-tight tracking-snug text-ash-900 md:text-5xl">
          {hasReboltInHeading ? (
            <>
              {headingParts[0]}
              <span className="text-rebolt-500">Rebolt</span>
              {headingParts[1]}
            </>
          ) : (
            heading
          )}
        </h2>

        <div className="overflow-x-auto rounded-2xl border border-ash-200 bg-white shadow-sm">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-ash-200 bg-ash-50">
                <th className="w-[30%] px-6 py-5 text-sm font-medium text-ash-500">Features</th>
                <th className="w-[35%] px-6 py-5 text-base font-semibold text-rebolt-500">Rebolt</th>
                <th className="w-[35%] px-6 py-5 text-base font-semibold text-ash-700">{competitorName}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.feature} className="border-b border-ash-100 last:border-b-0">
                  <td className="px-6 py-4 text-sm text-ash-500">{row.feature}</td>
                  <td className="px-6 py-4 text-sm font-semibold text-ash-900">{row.rebolt}</td>
                  <td className="px-6 py-4 text-sm text-ash-500">{row.competitor}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-10 text-center">
          <Link
            href="https://cal.com/team/builtright/launch"
            className="inline-flex items-center rounded-full bg-rebolt-500 px-8 py-3.5 text-base font-semibold text-white transition-colors hover:bg-rebolt-600"
          >
            Try Rebolt Now
          </Link>
        </div>
      </div>
    </section>
  );
}
